import { observer } from 'mobx-react-lite';
import { Link } from '@fluentui/react/lib/Link';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import PersonaName from 'owa-calendar-forms-common/lib/components/PersonaName';
import getAttendeeResponseCounts from 'owa-calendar-forms-common/lib/utils/getAttendeeResponseCounts';
import getAttendeeResponseString from 'owa-calendar-forms-common/lib/utils/getAttendeeResponseString';
import isAttendeeOrganizer from 'owa-calendar-forms-common/lib/utils/isAttendeeOrganizer';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import loc, { formatToArray } from 'owa-localize';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import * as React from 'react';

import {
    attendeeAccepted,
    attendeeTentativelyAccepted,
    attendeeDeclined,
    attendeeDidnotRespond,
    twoAttendeesAccepted,
    twoAttendeesTentativelyAccepted,
    twoAttendeesDeclined,
    twoAttendeesDidnotRespond,
    singleLineSeparator,
} from './AttendeeTrackingDetails.locstring.json';

const styles = require('./AttendeeTrackingDetails.scss');
const classNames = require('classnames/bind').bind(styles);

export interface AttendeeTrackingDetailsProps {
    event: CalendarEvent;
    className?: string;
    onAttendeeTrackingClicked?: (evt?: any) => void;
    colorScheme?: ColorScheme;
    singleLine?: boolean;
    usePlainTextForAttendees?: boolean;
    onlyShowSummary?: boolean;
}

const AttendeeTrackingDetails: React.FunctionComponent<AttendeeTrackingDetailsProps> = props => {
    const {
        event,
        className,
        onAttendeeTrackingClicked,
        colorScheme,
        singleLine,
        usePlainTextForAttendees,
        onlyShowSummary,
    } = props;
    const scheme = colorScheme || getCalendarEventColorScheme(event);
    const disabled = !onAttendeeTrackingClicked;

    const contentClassName = singleLine ? styles.singleLine : undefined;
    let useLink = true;

    let content: JSX.Element = null;
    const trackingAttendees = getAttendeesForTrackingDetails(event);

    if (!onlyShowSummary && (trackingAttendees.length === 1 || trackingAttendees.length === 2)) {
        if (trackingAttendees.length === 1) {
            useLink = !usePlainTextForAttendees;
            content = (
                <div className={contentClassName}>
                    {getTrackingDetailsForSingleAttendee(trackingAttendees[0])}
                </div>
            );
        } else if (trackingAttendees.length === 2) {
            useLink = !usePlainTextForAttendees;
            content = getTrackingDetailsForTwoAttendees(trackingAttendees, singleLine);
        }
    } else {
        content = (
            <div className={contentClassName}>
                {getAttendeeResponseString(getAttendeeResponseCounts(event))}
            </div>
        );
    }

    const onClick = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            if (onAttendeeTrackingClicked) {
                evt.stopPropagation();
                onAttendeeTrackingClicked();
            }
        },
        [onAttendeeTrackingClicked]
    );

    return useLink ? (
        <Link
            styles={generateLinkStyle(scheme)}
            className={classNames(styles.container, className, { singleLine: singleLine })}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}>
            {content}
        </Link>
    ) : (
        <div className={className}>{content}</div>
    );
};

export default observer(AttendeeTrackingDetails);

// Get the attendees data to be used to show the tracking details in meeting peek.
// We only show the tracking details if there are no more than 2 valid attendees.
// Otherwise, return null indicating that we won't show tracking details for this event.
function getAttendeesForTrackingDetails(item: CalendarEvent): AttendeeType[] {
    let trackingAttendees: AttendeeType[] = [];
    const organizer = item.Organizer;

    if (
        canGetTrackingAttendees(organizer, item.RequiredAttendees, trackingAttendees) &&
        canGetTrackingAttendees(organizer, item.OptionalAttendees, trackingAttendees)
    ) {
        return trackingAttendees;
    }

    return [];
}

function canGetTrackingAttendees(
    organizer: SingleRecipientType,
    attendeesList: AttendeeType[],
    trackingAttendees: AttendeeType[]
): boolean {
    if (!attendeesList) {
        return false;
    }

    for (let i = 0; i < attendeesList.length; ++i) {
        const attendee = attendeesList[i];
        if (attendee.Mailbox?.Name && !isAttendeeOrganizer(organizer, attendee)) {
            // Make sure the current user is the first attendee.
            if (
                attendee.Mailbox.EmailAddress ===
                getUserConfiguration().SessionSettings.UserEmailAddress
            ) {
                trackingAttendees.unshift(attendee);
            } else {
                trackingAttendees.push(attendee);
            }
            if (trackingAttendees.length > 2) {
                trackingAttendees = [];
                return false;
            }
        }
    }

    return trackingAttendees.length > 0;
}

function getTrackingDetailsForSingleAttendee(attendee: AttendeeType): JSX.Element {
    const person = (
        <PersonaName name={attendee.Mailbox.Name} emailAddress={attendee.Mailbox.EmailAddress} />
    );

    return <>{formatToArray(getStringTemplateForSingleAttendee(attendee.ResponseType), person)}</>;
}

function getTrackingDetailsForTwoAttendees(
    attendees: AttendeeType[],
    singleLine: boolean
): JSX.Element {
    const contentClassName = singleLine ? styles.singleLine : undefined;
    const twoAttendeesStringTemplate = getStringTemplateForTwoAttendee(attendees[0].ResponseType);

    // two attendees have different responses
    if (twoAttendeesStringTemplate != getStringTemplateForTwoAttendee(attendees[1].ResponseType)) {
        const firstAttendee = getTrackingDetailsForSingleAttendee(attendees[0]);
        const secondAttendee = getTrackingDetailsForSingleAttendee(attendees[1]);
        if (singleLine) {
            return (
                <div className={contentClassName}>
                    {formatToArray(loc(singleLineSeparator), firstAttendee, secondAttendee)}
                </div>
            );
        } else {
            return (
                <>
                    <div>{firstAttendee}</div>
                    <div>{secondAttendee}</div>
                </>
            );
        }
    }

    // two attendees have the same response
    const firstAttendee = (
        <PersonaName
            name={attendees[0].Mailbox.Name}
            emailAddress={attendees[0].Mailbox.EmailAddress}
        />
    );
    const secondAttendee = (
        <PersonaName
            name={attendees[1].Mailbox.Name}
            emailAddress={attendees[1].Mailbox.EmailAddress}
        />
    );

    return (
        <div className={contentClassName}>
            {formatToArray(twoAttendeesStringTemplate, firstAttendee, secondAttendee)}
        </div>
    );
}

function getStringTemplateForSingleAttendee(response: ResponseTypeType): string {
    switch (response) {
        case 'Accept':
            return loc(attendeeAccepted);
        case 'Tentative':
            return loc(attendeeTentativelyAccepted);
        case 'Decline':
            return loc(attendeeDeclined);
        default:
            break;
    }

    return loc(attendeeDidnotRespond);
}

function getStringTemplateForTwoAttendee(response: ResponseTypeType): string {
    switch (response) {
        case 'Accept':
            return loc(twoAttendeesAccepted);
        case 'Tentative':
            return loc(twoAttendeesTentativelyAccepted);
        case 'Decline':
            return loc(twoAttendeesDeclined);
        default:
            break;
    }

    return loc(twoAttendeesDidnotRespond);
}
